import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import OnboardingWizard from '../components/OnboardingWizard';
import theme from '../theme';
import { User, Award, CreditCard, Eye } from 'lucide-react';

const PartnerOnboarding = ({ setUser }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    licenses: [],
    affiliations: '',
    specialties: [],
    kycPassed: false,
    membershipTier: '',
    billingCycle: 'annual',
    publicProfile: true,
    bio: '',
    website: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleComplete = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/onboarding/broker/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        const data = await res.json();
        // P2.1: Update user state with returned user data
        if (data.user && setUser) {
          setUser(data.user);
        }
        navigate('/broker/dashboard');
      } else {
        alert('Onboarding failed. Please try again.');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      alert('Error completing onboarding');
    }
  };

  const specialtiesOptions = ['Luxury Fashion', 'Streetwear', 'Sustainable Fashion', 'High Fashion', 'Accessories', 'Footwear'];

  const steps = [
    {
      label: 'Professional Profile',
      content: (
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{ width: '48px', height: '48px', background: `${theme.colors.primary}15`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <User size={24} color={theme.colors.primary} />
            </div>
            <div>
              <h3 style={{ fontFamily: theme.fonts.heading, fontSize: '20px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '4px' }}>Share licenses, affiliations, specialties</h3>
              <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>Help creators find the right portfolio partner</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '8px' }}>Full Name *</label>
              <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Doe" style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, fontSize: '15px', outline: 'none' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '8px' }}>License Number</label>
              <input type="text" value={formData.licenses[0] || ''} onChange={(e) => setFormData({...formData, licenses: [e.target.value]})} placeholder="BRK-2024-001" style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, fontSize: '15px', outline: 'none' }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '8px' }}>Specialties</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                {specialtiesOptions.map(spec => (
                  <button key={spec} type="button" onClick={() => {
                    const current = formData.specialties;
                    if (current.includes(spec)) {
                      setFormData({...formData, specialties: current.filter(s => s !== spec)});
                    } else {
                      setFormData({...formData, specialties: [...current, spec]});
                    }
                  }} style={{ padding: '10px', border: `2px solid ${formData.specialties.includes(spec) ? theme.colors.primary : theme.colors.borders}`, borderRadius: theme.radius.md, background: formData.specialties.includes(spec) ? `${theme.colors.primary}10` : 'transparent', fontSize: '14px', cursor: 'pointer' }}>{spec}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      label: 'KYC / Licensing',
      content: (
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{ width: '48px', height: '48px', background: `${theme.colors.primary}15`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Award size={24} color={theme.colors.primary} />
            </div>
            <div>
              <h3 style={{ fontFamily: theme.fonts.heading, fontSize: '20px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '4px' }}>Verify your identity and credentials</h3>
              <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>Mock Persona KYC integration</p>
            </div>
          </div>
          <div style={{ padding: '24px', border: `2px dashed ${theme.colors.borders}`, borderRadius: theme.radius.card, textAlign: 'center', background: theme.colors.bg.secondary }}>
            <p style={{ fontSize: '16px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '12px' }}>Identity Verification</p>
            <p style={{ fontSize: '14px', color: theme.colors.text.secondary, marginBottom: '16px' }}>Mock: Persona KYC</p>
            <button onClick={() => setFormData({...formData, kycPassed: true})} style={{ padding: '12px 32px', background: formData.kycPassed ? theme.colors.success : theme.colors.primary, border: 'none', borderRadius: theme.radius.cta, color: '#FFF', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>{formData.kycPassed ? '✓ KYC Verified' : 'Start Verification'}</button>
          </div>
        </div>
      )
    },
    {
      label: 'Membership',
      content: (
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{ width: '48px', height: '48px', background: `${theme.colors.primary}15`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CreditCard size={24} color={theme.colors.primary} />
            </div>
            <div>
              <h3 style={{ fontFamily: theme.fonts.heading, fontSize: '20px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '4px' }}>Pick a plan to accept portfolios</h3>
              <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>Stripe integration for membership billing</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            {['basic', 'pro', 'elite'].map(tier => (
              <button key={tier} type="button" onClick={() => setFormData({...formData, membershipTier: tier})} style={{ padding: '20px', border: `2px solid ${formData.membershipTier === tier ? theme.colors.primary : theme.colors.borders}`, borderRadius: theme.radius.card, background: formData.membershipTier === tier ? `${theme.colors.primary}10` : theme.colors.bg.card, cursor: 'pointer', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: theme.colors.text.primary, textTransform: 'capitalize', marginBottom: '8px' }}>{tier}</div>
                <div style={{ fontSize: '24px', fontWeight: '700', color: theme.colors.primary, marginBottom: '4px' }}>${tier === 'basic' ? '99' : tier === 'pro' ? '249' : '499'}</div>
                <div style={{ fontSize: '12px', color: theme.colors.text.secondary }}>/month</div>
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '16px' }}>
            <button type="button" onClick={() => setFormData({...formData, billingCycle: 'monthly'})} style={{ padding: '8px 16px', border: `1px solid ${formData.billingCycle === 'monthly' ? theme.colors.primary : theme.colors.borders}`, borderRadius: theme.radius.md, background: formData.billingCycle === 'monthly' ? `${theme.colors.primary}10` : 'transparent', fontSize: '14px', cursor: 'pointer' }}>Monthly</button>
            <button type="button" onClick={() => setFormData({...formData, billingCycle: 'annual'})} style={{ padding: '8px 16px', border: `1px solid ${formData.billingCycle === 'annual' ? theme.colors.primary : theme.colors.borders}`, borderRadius: theme.radius.md, background: formData.billingCycle === 'annual' ? `${theme.colors.primary}10` : 'transparent', fontSize: '14px', cursor: 'pointer' }}>Annual (Save 20%)</button>
          </div>
        </div>
      )
    },
    {
      label: 'Directory Profile',
      content: (
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
            <div style={{ width: '48px', height: '48px', background: `${theme.colors.primary}15`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Eye size={24} color={theme.colors.primary} />
            </div>
            <div>
              <h3 style={{ fontFamily: theme.fonts.heading, fontSize: '20px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '4px' }}>Set your public profile & visibility</h3>
              <p style={{ fontSize: '14px', color: theme.colors.text.secondary }}>Help creators discover you</p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                <input type="checkbox" checked={formData.publicProfile} onChange={(e) => setFormData({...formData, publicProfile: e.target.checked})} style={{ width: '20px', height: '20px', cursor: 'pointer' }} />
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: theme.colors.text.primary }}>Show in Directory</div>
                  <div style={{ fontSize: '13px', color: theme.colors.text.secondary }}>Make your profile visible to creators</div>
                </div>
              </label>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '8px' }}>Bio</label>
              <textarea value={formData.bio} onChange={(e) => setFormData({...formData, bio: e.target.value})} placeholder="Tell creators about your experience..." rows={4} style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, fontSize: '15px', outline: 'none', resize: 'vertical', fontFamily: theme.fonts.ui }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: theme.colors.text.primary, marginBottom: '8px' }}>Website</label>
              <input type="url" value={formData.website} onChange={(e) => setFormData({...formData, website: e.target.value})} placeholder="https://yoursite.com" style={{ width: '100%', padding: '12px 16px', border: `1px solid ${theme.colors.borders}`, borderRadius: theme.radius.md, fontSize: '15px', outline: 'none' }} />
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <OnboardingWizard
      steps={steps}
      currentStep={currentStep}
      onNext={() => {
        if (currentStep === 0 && !formData.name) { alert('Please enter your name'); return; }
        if (currentStep === 1 && !formData.kycPassed) { alert('Please complete KYC verification'); return; }
        if (currentStep === 2 && !formData.membershipTier) { alert('Please select a membership tier'); return; }
        setCurrentStep(currentStep + 1);
      }}
      onBack={() => setCurrentStep(currentStep - 1)}
      onComplete={handleComplete}
      title="Portfolio Partner Onboarding"
      subtitle="Join as a verified portfolio partner"
    />
  );
};

export default PartnerOnboarding;